import { useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { generateEmailFromUsername } from "@/lib/authIdentity";
import { mapPasswordErrorMessage } from "@/lib/authPasswordError";
import { BASE_URL } from "@/constants/app";

// ── Types ──────────────────────────────────────────────

export type AuthResultType = "signed_in" | "signed_up" | "referral_bonus";

export interface AuthResult {
  type: AuthResultType;
  userId: string;
}

// ── Validation ─────────────────────────────────────────

const USERNAME_RE = /^[a-zA-Z0-9_ąćęłńóśźżĄĆĘŁŃÓŚŹŻ]+$/;

export const validateUsername = (raw: string): string | null => {
  const v = raw.trim();
  if (v.length < 3) return "Nazwa użytkownika musi mieć min. 3 znaki";
  if (v.length > 30) return "Nazwa użytkownika może mieć maks. 30 znaków";
  if (!USERNAME_RE.test(v)) return "Nazwa może zawierać tylko litery, cyfry i podkreślniki";
  return null;
};

export const validateEmail = (email: string): string | null => {
  const v = email.trim();
  if (!v) return "Podaj adres e-mail";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) return "Podaj poprawny adres e-mail";
  return null;
};

/** Accepts email or username for login fields */
export const validateIdentifier = (raw: string): string | null => {
  const v = raw.trim();
  if (!v) return "Podaj e-mail lub nazwę użytkownika";
  if (v.length < 3) return "Minimum 3 znaki";
  return null;
};

export const validatePassword = (
  password: string,
  isRegistration: boolean
): string | null => {
  if (password.length < 8) return "Hasło musi mieć co najmniej 8 znaków";
  if (isRegistration && !/(?=.*[a-z])(?=.*[A-Z0-9])/.test(password)) {
    return "Hasło powinno zawierać małe i duże litery lub cyfry";
  }
  return null;
};

// ── Internal helpers ───────────────────────────────────

/** Determine if input looks like an email */
function isEmail(input: string): boolean {
  return input.includes("@") && !input.endsWith("@biozadania.local");
}

/** Resolve identifier to email for Supabase auth */
function resolveEmail(identifier: string): string {
  const trimmed = identifier.trim();
  if (isEmail(trimmed)) return trimmed;
  // Username login → pseudo-email (legacy + new username logins)
  return generateEmailFromUsername(trimmed);
}

async function createProfile(
  userId: string,
  username: string,
  termsAccepted: boolean
) {
  const payload: Record<string, unknown> = {
    user_id: userId,
    username,
    username_confirmed: false,
  };
  if (termsAccepted) {
    payload.terms_accepted_at = new Date().toISOString();
  }

  const { error } = await supabase
    .from("profiles")
    .upsert(payload as any, { onConflict: "user_id" });

  if (error) console.error("Error creating profile:", error);
}

async function processReferral(userId: string): Promise<boolean> {
  const refCode = sessionStorage.getItem("referral_code");
  if (!refCode) return false;

  try {
    const { data } = await supabase.rpc("process_referral", {
      p_referred_id: userId,
      p_code: refCode,
    });
    sessionStorage.removeItem("referral_code");
    return !!data;
  } catch (err) {
    console.error("Referral processing error:", err);
    return false;
  }
}

// ── Hook ───────────────────────────────────────────────

interface UseAuthActionsOptions {
  termsAccepted?: boolean;
}

export const useAuthActions = (opts: UseAuthActionsOptions = {}) => {
  const { termsAccepted = false } = opts;

  /**
   * Sign in with email or username + password.
   */
  const signIn = useCallback(
    async (identifier: string, password: string): Promise<AuthResult> => {
      const email = resolveEmail(identifier.trim());

      let { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      // Legacy-account fallback: pre-rebrand pseudo-emails used @biomatura.local
      if (error && email.endsWith("@biozadania.local")) {
        const legacy = email.replace(/@biozadania\.local$/, "@biomatura.local");
        const retry = await supabase.auth.signInWithPassword({ email: legacy, password });
        if (!retry.error) {
          data = retry.data;
          error = null;
        }
      }

      if (error) {
        if (error.message.includes("Invalid login credentials")) {
          throw new Error("Nieprawidłowy e-mail/nazwa użytkownika lub hasło");
        }
        throw new Error(error.message);
      }

      return { type: "signed_in", userId: data.user.id };
    },
    []
  );

  /**
   * Combined flow used by LoginPromptModal:
   * 1. Try signing in
   * 2. If input is email and sign-in fails → register
   */
  const signUpOrSignIn = useCallback(
    async (identifier: string, password: string): Promise<AuthResult> => {
      const trimmed = identifier.trim();
      const email = resolveEmail(trimmed);

      // Step 1 – try signing in
      let { data: signInData, error: signInError } =
        await supabase.auth.signInWithPassword({ email, password });

      // Legacy pseudo-email fallback (pre-rebrand accounts).
      if (signInError && email.endsWith("@biozadania.local")) {
        const legacy = email.replace(/@biozadania\.local$/, "@biomatura.local");
        const retry = await supabase.auth.signInWithPassword({ email: legacy, password });
        if (!retry.error) {
          signInData = retry.data;
          signInError = null;
        }
      }

      if (!signInError && signInData.user) {
        return { type: "signed_in", userId: signInData.user.id };
      }

      // If input is a username (not email), don't auto-register
      if (!isEmail(trimmed)) {
        throw new Error("Nieprawidłowa nazwa użytkownika lub hasło");
      }

      // Step 2 – register with real email
      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email: trimmed,
          password,
          options: {
            emailRedirectTo: BASE_URL,
          },
        });

      if (signUpError) {
        const passwordIssue = mapPasswordErrorMessage(signUpError.message);
        if (passwordIssue) throw new Error(passwordIssue);
        if (/signups? not allowed/i.test(signUpError.message)) {
          throw new Error("Rejestracja nowych kont jest tymczasowo wyłączona. Jeśli masz już konto, zaloguj się normalnie.");
        }
        if (signUpError.message?.toLowerCase().includes("already registered")) {
          throw new Error("Nieprawidłowe hasło. Spróbuj ponownie.");
        }
        throw new Error(signUpError.message || "Błąd rejestracji");
      }

      if (!signUpData.user) {
        throw new Error("Błąd rejestracji — brak danych użytkownika");
      }

      // Create profile with auto-generated username from email
      const autoUsername = trimmed.split("@")[0].replace(/[^a-zA-Z0-9]/g, "").substring(0, 20) || `user${Date.now().toString().slice(-6)}`;
      await createProfile(signUpData.user.id, autoUsername, termsAccepted);

      const referralSuccess = await processReferral(signUpData.user.id);

      return {
        type: referralSuccess ? "referral_bonus" : "signed_up",
        userId: signUpData.user.id,
      };
    },
    [termsAccepted]
  );

  /**
   * Explicit registration with real email (Auth.tsx registration tab).
   */
  const signUp = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      const trimmedEmail = email.trim();

      const { data: signUpData, error: signUpError } =
        await supabase.auth.signUp({
          email: trimmedEmail,
          password,
          options: {
            emailRedirectTo: BASE_URL,
          },
        });

      if (signUpError) {
        const passwordIssue = mapPasswordErrorMessage(signUpError.message);
        if (passwordIssue) throw new Error(passwordIssue);
        if (/signups? not allowed/i.test(signUpError.message)) {
          throw new Error("Rejestracja nowych kont jest tymczasowo wyłączona. Jeśli masz już konto, zaloguj się normalnie.");
        }
        if (signUpError.message.includes("already registered")) {
          throw new Error("Ten adres e-mail jest już zarejestrowany");
        }
        throw new Error(signUpError.message);
      }

      if (!signUpData.user) {
        throw new Error("Błąd rejestracji — brak danych użytkownika");
      }

      const autoUsername = trimmedEmail.split("@")[0].replace(/[^a-zA-Z0-9]/g, "").substring(0, 20) || `user${Date.now().toString().slice(-6)}`;
      await createProfile(signUpData.user.id, autoUsername, termsAccepted);

      const referralSuccess = await processReferral(signUpData.user.id);

      return {
        type: referralSuccess ? "referral_bonus" : "signed_up",
        userId: signUpData.user.id,
      };
    },
    [termsAccepted]
  );

  /**
   * Send password reset email.
   */
  const resetPassword = useCallback(
    async (email: string): Promise<{ error: string | null }> => {
      const trimmed = email.trim();
      const { error } = await supabase.auth.resetPasswordForEmail(trimmed, {
        redirectTo: `${BASE_URL}/reset-password`,
      });
      if (error) return { error: error.message };
      return { error: null };
    },
    []
  );

  return {
    signIn,
    signUp,
    signUpOrSignIn,
    resetPassword,
    validateUsername,
    validateEmail,
    validateIdentifier,
    validatePassword,
  };
};
